import React from 'react'
import cn from 'classnames'
import { Edit, Pin } from '@groovehq/internal-design-system/lib/assets/icons'
import Menu from '@groovehq/internal-design-system/lib/components/Menu/Menu'
import Link from 'redux-first-router-link'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'

import { isEmpty } from 'util/arrays'
import storage from 'util/storage'

import { doOpenNewTicketPage } from 'actions/pages'
import Mailbox from './Mailbox'
import Starred from './Starred'

import { styles } from './styles'
import RefreshButton from '../Sidebar/RefreshButton'
import BookDemoCard from './BookDemoCard'

class Leftnav extends React.PureComponent {
  // eslint-disable-next-line react/sort-comp
  DEFAULT_OPEN_SECTIONS = { Starred: true, Folders: true }

  constructor(props) {
    super(props)
    this.state = {
      starredOpen: !props.mailboxId,
      expandedSections: this.leftNavOpenSectionsFromStorage(),
      expandedSectionsAtLeastOnce:
        storage.get('inbox-leftnav-has-ever-expanded-sections') || [],
      openNewCoversationLink: doOpenNewTicketPage(),
    }
  }

  leftNavOpenSectionsFromStorage = () => {
    const values =
      storage.get('inbox-leftnav-open-sections') || this.DEFAULT_OPEN_SECTIONS
    let valuesAsObj = values

    if (Array.isArray(values)) {
      // old data structure which is an array of strings
      valuesAsObj = values.reduce((accumulator, section) => {
        // eslint-disable-next-line no-param-reassign
        accumulator[section] = true

        return accumulator
      }, {})
    }

    return valuesAsObj
  }

  onMailboxClick = () => {
    this.setState({ starredOpen: false })
  }

  onStarredClick = () => {
    this.setState({ starredOpen: true })
  }

  // When we first load the app
  //   - if we hit a ticket page
  //     - we expand the sidebar section for that ticket's mailbox
  //
  //   - if we hit a search page
  //     - if it has a mailbox ID in the URL
  //       - we open the corresponding Mailbox in the sidebar
  //     - if it is a pinned search (and they are visible)
  //       - we open the first section (assumes Pinned always at the top)
  //     - otherwise open All Inboxes, if you have the preference set
  //     - otherwise just expand the first item (Pinned Searches)
  //
  // This logic can be simplified further given;
  //
  // 1. The currentMailboxId won't be set if its a Pinned search (because we
  // remove mailbox when pinning a search).
  //
  // 2. Showing the very first section (Pinnned, All Inboxes or the first
  // Mailbox) will do otherwise.
  //
  // Thus
  //   - if the mailboxId is set, use its' index.
  //      - this includes All Inboxes which has a null ID,
  //   - Otherwise return 0 for the first section
  //
  getInitialExpandedSectionIndex = shouldShowStarredSearches => {
    const { mailboxes, currentMailboxId, mailboxIdFromTicket } = this.props
    const mailboxId = mailboxIdFromTicket || currentMailboxId

    if (!mailboxId) return 0

    const indexOffset = shouldShowStarredSearches ? 1 : 0
    const index =
      Math.max(0, mailboxes.findIndex(e => e.id === mailboxId)) + indexOffset

    return index
  }

  isSectionExpanded = sectionTitle =>
    this.state.expandedSections[sectionTitle] || false

  hasEverToggledSection = sectionTitle =>
    this.state.expandedSections[sectionTitle] !== undefined

  expandSection = sectionTitle => {
    this.setSectionCollapsedState(sectionTitle, true)
  }

  collapseSection = sectionTitle => {
    this.setSectionCollapsedState(sectionTitle, false)
  }

  setSectionCollapsedState = (sectionTitle, isExpanded) => {
    const { expandedSections: oldExpandedSections } = this.state
    const expandedSections = {
      ...oldExpandedSections,
      [sectionTitle]: isExpanded,
    }

    storage.set('inbox-leftnav-open-sections', expandedSections)
    this.setState({ expandedSections })
  }

  toggleSection = sectionTitle => {
    if (this.isSectionExpanded(sectionTitle)) {
      return this.collapseSection(sectionTitle)
    }
    return this.expandSection(sectionTitle)
  }

  toggleTeamSection = () => {
    this.toggleSection('Team')
  }

  toggleTagsSection = () => this.toggleSection('Tags')

  render() {
    const {
      canSeePinnedSearches,
      mailboxes,
      mailboxId,
      starredSearches,
      doOpenSearchPage,
      doStartSearchTimer,
      prefersUnifiedInbox,
      is3ColumnView,
      showBookDemoCard,
    } = this.props
    const { starredOpen, openNewCoversationLink } = this.state
    if (!mailboxes) return null

    const shouldShowStarredSearches =
      canSeePinnedSearches && starredSearches && !isEmpty(starredSearches)
    const starredSearchesActive = shouldShowStarredSearches && starredOpen
    const wantsCompactMode = prefersUnifiedInbox && !shouldShowStarredSearches

    const headerWithComposeButton = (
      <React.Fragment>
        <div className="grui flex">
          <Button
            as={Link}
            type="tertiary"
            size="small"
            css={styles.composeButton}
            customIcon={<Edit />}
            to={openNewCoversationLink}
          >
            Compose
          </Button>
        </div>
        <div className="grui flex">
          <span className="grui mt-18">Inbox</span>
        </div>
      </React.Fragment>
    )

    const footer = showBookDemoCard && <BookDemoCard />

    return (
      <Menu
        title={headerWithComposeButton}
        controls={
          !is3ColumnView && (
            <div className="grui mt-18">
              <RefreshButton />
            </div>
          )
        }
        tight
        animate={false}
        footer={footer}
        className={cn('grui relative', footer && 'with-footer')}
        css={styles.menu}
        dataTestId="inbox-left-nav"
      >
        <>
          {shouldShowStarredSearches && (
            <Menu.Item
              icon={<Pin className="grui mr-3" />}
              css={styles.pinnedItem}
              className="grui pl-4"
              onClick={this.onStarredClick}
              value="menu-starred"
              subMenu={
                starredOpen && <Starred starredSearches={starredSearches} />
              }
            >
              Pinned
            </Menu.Item>
          )}
          {mailboxes.map(mailbox => {
            let isCurrent = false
            // when starred searches are open, all inboxes should remain closed
            if (!starredSearchesActive) {
              // in unified inbox mode, there unified inbox should always be active
              // in split inbox mode, only the current context mailbox should be open
              isCurrent = prefersUnifiedInbox || mailbox.id === mailboxId
            }
            return (
              <Mailbox
                key={mailbox.id || mailbox.name} // mailbox.id can be null (for 'All inboxes')
                onClick={this.onMailboxClick}
                mailboxId={mailbox.id}
                isCurrent={isCurrent}
                wantsCompactMode={wantsCompactMode}
                isTeamSectionOpen={this.isSectionExpanded('Team')}
                isTagsSectionOpen={this.isSectionExpanded('Tags')}
                toggleTeamSection={this.toggleTeamSection}
                toggleTagsSection={this.toggleTagsSection}
                doOpenSearchPage={doOpenSearchPage}
                doStartSearchTimer={doStartSearchTimer}
                hasEverToggledTeamSection={this.hasEverToggledSection('Team')}
              />
            )
          })}
        </>
      </Menu>
    )
  }
}

export default Leftnav
